import {Card, CardContent, Grid, Typography, useTheme} from "@mui/material";
import React from "react";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../../common/form-fields";
import {useTranslation} from "react-i18next";
import {FormElementI, SelectElementModelFE,} from "../../../../api/types/generic";
import {CommercialCategorySelectFE} from "../../../../api/types/commercialCategory";
import {useFormContext} from "react-hook-form";
import {FormRateModel} from "../../../../api/types/rate";
import {DateType} from "@date-io/type";
import {unitsEnumAtom} from "../../../../state/rentalPeriodsRecoil";
import {useRecoilState} from "recoil";
import DealerAndTenant from "../../../../common/components/DealerAndTenant";

const MainDefinitionComponent = ({
                                     commercialCategory,
                                     commercialSegment,
                                     name,
                                     description,
                                     tpUnit,
                                     carGroup,
                                     pickupFrom,
                                     pickupTo,
                                     reservationFrom,
                                     reservationTo,
                                     tax,
                                     dealer,
                                     legalEntity,
                                 }: {
    commercialCategory: FormElementI<CommercialCategorySelectFE>;
    commercialSegment: FormElementI<SelectElementModelFE>;
    name: FormElementI<string>;
    description: FormElementI<string>;
    tpUnit: FormElementI<string>;
    carGroup: FormElementI<string>;
    reservationFrom: FormElementI<DateType | null>;
    reservationTo: FormElementI<DateType | null>;
    pickupFrom: FormElementI<DateType | null>;
    pickupTo: FormElementI<DateType | null>;
    tax: FormElementI<boolean>;
    dealer: FormElementI<SelectElementModelFE>;
    legalEntity: FormElementI<SelectElementModelFE>;
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use Form Context
    const {register, getValues} = useFormContext<FormRateModel>();

    //Use Recoil
    const [unitsEnum] = useRecoilState(unitsEnumAtom);
    // Use Form Context
    const form = useFormContext<FormRateModel>();
    return (
        <Card>
            <CardContent>
                <Grid container spacing={2} p={2}>
                    <Typography sx={{color: theme.palette.primary.dark}}>
                        {t("pricing:rate.form.labels.mainDefinition")}
                    </Typography>
                </Grid>

                {/* MAIN DETAIL */}
                <Grid container spacing={2} py={2}>
                    <DealerAndTenant
                        readOnly={dealer.readonly}
                        md={6}
                        legalEntityName={"mainDefinition.legalEntity"}
                        dealerName={"mainDefinition.dealer"}
                        dealerIsVisible={true}
                        idForReadOnly={form.getValues("id")}
                    />
                    <Grid item xs={12} md={6}>
                        {/* COMMERCIAL SEGMENT */}
                        <FormAutocomplete
                            {...register("mainDefinition.commercialSegment")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.commercialSegment"
                            )}*`}
                            readOnly={commercialSegment.readonly}
                            options={commercialSegment.options || []}
                            isOptionEqualToValue={(option, value) =>
                                option?.code === value?.code
                            }
                            handleChange={commercialSegment.handleChange as Function}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* COMMERCIAL CATEGORY */}
                        <FormAutocomplete
                            {...register("mainDefinition.commercialCategory")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.commercialCategory"
                            )}*`}
                            readOnly={commercialCategory.readonly}
                            options={commercialCategory.options || []}
                            isOptionEqualToValue={(option, value) =>
                                option?.code === value?.code
                            }
                            handleChange={commercialCategory.handleChange as Function}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* CAR GROUP */}
                        <FormAutocomplete
                            {...register("mainDefinition.carGroup")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.carGroup"
                            )}*`}
                            readOnly={carGroup.readonly}
                            options={carGroup.options || []}
                            isOptionEqualToValue={(option, value) => option === value}
                            handleChange={carGroup.handleChange as Function}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* NAME */}
                        <FormTextField
                            {...register("mainDefinition.name")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.name"
                            )}*`}
                            readOnly={name.readonly}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* DESCRIPTION */}
                        <FormTextField
                            {...register("mainDefinition.description")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.description"
                            )}`}
                            readOnly={description.readonly}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* UNIT */}
                        <FormAutocomplete
                            {...register("mainDefinition.tpUnit")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.unit"
                            )}*`}
                            readOnly={tpUnit.readonly}
                            options={unitsEnum || []}
                            isOptionEqualToValue={(option: string, value: string) =>
                                option === value
                            }
                            handleChange={tpUnit.handleChange as Function}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2} py={2}>
                    <Grid item xs={12} md={6}>
                        {/* RESERVATION FROM */}
                        <FormDateTimePicker
                            {...register("mainDefinition.reservationFrom")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.reservationFrom"
                            )}*`}
                            readOnly={reservationFrom.readonly}
                            minDate={reservationFrom.dateMinOrMax}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* RESERVATION TO */}
                        <FormDateTimePicker
                            {...register("mainDefinition.reservationTo")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.reservationTo"
                            )}*`}
                            readOnly={reservationTo.readonly}
                            minDate={reservationTo.dateMinOrMax}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* PICKUP DATE FROM */}
                        <FormDateTimePicker
                            {...register("mainDefinition.pickupFrom")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.pickupDateFrom"
                            )}*`}
                            readOnly={pickupFrom.readonly}
                            minDate={pickupFrom.dateMinOrMax}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* PICKUP DATE TO */}
                        <FormDateTimePicker
                            {...register("mainDefinition.pickupTo")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.pickupDateTo"
                            )}*`}
                            readOnly={pickupTo.readonly}
                            minDate={pickupTo.dateMinOrMax}
                        />
                    </Grid>
                    {/* TAX */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{display: "flex", alignItems: "flex-end"}}
                    >
                        {/* PUBLIC */}
                        <FormCustomSwitch
                            {...register("mainDefinition.tax")}
                            label={`${t(
                                "pricing:rate.form.labels.mainDefinitionLabels.tax"
                            )}`}
                            readOnly={tax.readonly}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default MainDefinitionComponent;
